//@flow
import { compose, withProps, withState } from 'recompose';
import withHandlers from 'recompose/withHandlers';
import withStateHandlers from 'recompose/withStateHandlers';
import pure from 'recompose/pure';
import lifecycle from 'recompose/lifecycle';
import * as Immutable from 'immutable';

import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import history from 'common/middleware/history';

import { FitToBoundStrategyEnum } from 'common/enums/MapEnum';

import {
    ScreenSizeStates,
    ColumnResizeHelper
} from 'common/components/core/ColumnResizer/ColumnResizeHelper';
import type { VisitIndexReferenceType } from 'common/types/common.types';

export type FitToBoundStrategyType = 'NEXT_RENDER' | 'ONBOUNDS_CHANGE';
export type SelectedVisitReferenceType = {
    tourIndex: number,
    visitIndex: VisitIndexReferenceType,
    sourceId: string
};

const resizeHandler = {
    componentDidMount() {
        this.props.handleWindowResize();
        window.addEventListener('resize', this.props.handleWindowResize);
    },
    componentWillUnmount() {
        window.removeEventListener('resize', this.props.handleWindowResize);
    }
};

const GANTT_MIN_WIDTH = 300;
const DEFAULT_GANTT_AREA_RATIO = 0.6;

const ColumnResizerState = compose(
    withStateHandlers(
        () => {
            const containerWidth = window.innerWidth - 60;
            const {
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            } = ColumnResizeHelper.getDefaultWidths({
                containerWidth,
                leftPaneWidth: containerWidth * DEFAULT_GANTT_AREA_RATIO
            });
            return {
                containerWidth,
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition,
                screenSizeState: ScreenSizeStates.Default,
                nextScreenSizeState: ScreenSizeStates.Default,
                isResizing: false
            };
        },
        {
            setNextScreenSizeState: () => nextScreenSizeState => ({
                nextScreenSizeState
            }),
            setIsResizing: () => isResizing => ({
                isResizing
            }),
            setUpdatedScreenSize: () => ({
                screenSizeState,
                nextScreenSizeState,
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            }) => ({
                screenSizeState,
                nextScreenSizeState,
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            }),
            setDefaultWidths: () => ({
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition,
                containerWidth
            }) => ({
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition,
                containerWidth
            })
        }
    ),
    withHandlers({
        onScreenSizeStateChange: ({
            containerWidth,
            setUpdatedScreenSize
        }) => screenSizeState => {
            const {
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            } = ColumnResizeHelper.getWidthsFromScreenSizeState({
                screenSizeState,
                containerWidth,
                leftPaneMinWidth: GANTT_MIN_WIDTH,
                defaultRatio: DEFAULT_GANTT_AREA_RATIO
            });
            setUpdatedScreenSize({
                screenSizeState,
                nextScreenSizeState: null,
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            });
        }
    }),
    withHandlers({
        onResizeButtonClick: ({
            onScreenSizeStateChange
        }) => screenSizeState => {
            onScreenSizeStateChange(screenSizeState);
        }
    }),
    withHandlers({
        handleWindowResize: ({ setDefaultWidths, screenSizeState }) => () => {
            const containerWidth = window.innerWidth - 60;
            const {
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition
            } = ColumnResizeHelper.getWidthsFromScreenSizeState({
                screenSizeState,
                containerWidth,
                leftPaneMinWidth: GANTT_MIN_WIDTH,
                defaultRatio: DEFAULT_GANTT_AREA_RATIO
            });
            setDefaultWidths({
                leftPaneWidth,
                rightPaneWidth,
                columnResizerPosition,
                containerWidth
            });
        },
        onColumnResize: ({
            containerWidth,
            screenSizeState,
            setNextScreenSizeState,
            setIsResizing
        }) => (e, data) => {
            const { x } = data;
            const newScreenSizeState = ColumnResizeHelper.getNewScreenSizeState(
                {
                    screenSizeState,
                    currentXPos: x,
                    containerWidth,
                    leftPaneMinWidth: GANTT_MIN_WIDTH,
                    defaultRatio: DEFAULT_GANTT_AREA_RATIO
                }
            );
            setNextScreenSizeState(newScreenSizeState);
            setIsResizing(true);
        },
        onColumnResizeStop: ({
            containerWidth,
            screenSizeState,
            setIsResizing,
            onResizeButtonClick
        }) => (e, data) => {
            const { x } = data;
            const newScreenSizeState = ColumnResizeHelper.getNewScreenSizeState(
                {
                    screenSizeState,
                    currentXPos: x,
                    containerWidth,
                    leftPaneMinWidth: GANTT_MIN_WIDTH,
                    defaultRatio: DEFAULT_GANTT_AREA_RATIO
                }
            );
            setIsResizing(false);
            onResizeButtonClick(newScreenSizeState);
        }
    })
);

const enhancer = compose(
    ColumnResizerState,
    withProps(props => {
        const { clientId, planId } = props;
        const frozenPlanUrl = history.createHref({
            pathname: `/client/${clientId}/pjp/plan/${planId}/metrics/planView/allocated`
        });
        return { frozenPlanUrl, GANTT_MIN_WIDTH };
    }),
    withState('updateAsMapMoves', 'setUpdateAsMapMoves', false),
    withState('isMapLoading', 'setIsMapLoading', false),
    withState('selectedTourIds', 'setSelectedTourIds', Immutable.List()),
    withState('visibleTourIds', 'setVisibleTourIds', Immutable.List<string>()),
    withState(
        'visibleTourSummaries',
        'setVisibleTourSummaries',
        Immutable.List()
    ),
    withState('forceFitBounds', 'setForceFitBounds', false),
    withState(
        'fitToBoundStrategy',
        'setFitToBoundsStrategy',
        FitToBoundStrategyEnum.NEXT_RENDER
    ),
    withHandlers({
        setBoundStrategyAndUpdate: ({
            setForceFitBounds,
            setFitToBoundsStrategy
        }) => () => {
            setFitToBoundsStrategy(FitToBoundStrategyEnum.ONBOUNDS_CHANGE);
            setForceFitBounds(true);
        }
    }),
    withState('unitConverter', 'setUnitConverter', ({ dimensionsMapping }) => {
        return new UnitConversionHelper({
            dimensionsMapping
        });
    }),
    withState('mapRef', 'setMapRef', null),
    lifecycle(resizeHandler),
    withHandlers({
        getPlanMapRef: ({ setMapRef }) => ref => {
            setMapRef(ref);
        },
        forceMapFitToBounds: ({
            setForceFitBounds,
            setFitToBoundsStrategy
        }) => (
            forceFitBounds: boolean,
            fitToBoundsStrategy: FitToBoundStrategyType
        ) => {
            setForceFitBounds(forceFitBounds);
            setFitToBoundsStrategy(fitToBoundsStrategy);
        },
        closeMapSpiderPopup: ({ mapRef }) => () => {
            const planMapRef = mapRef
                ?.getWrappedInstance()
                ?.getWrappedInstance();
            planMapRef?.closeSpiderPopup();
        }
    }),
    withHandlers({
        closeAllMapPopups: ({ closeMapSpiderPopup }) => () => {
            closeMapSpiderPopup();
        },
        selectTourVisitAndCloseSpiderPopup: ({ closeMapSpiderPopup }) => () => {
            closeMapSpiderPopup();
        }
    }),
    pure
);

export default enhancer;
